






















































































































































import { Component, Ref, Vue } from "vue-property-decorator";
import PreviousPageArrow from "@/components/PreviousPageArrow.vue";
import {
  DocumentType,
  DocumentSide,
  UploadPayload,
} from "@/modules/document/types";
import FrontOfLicenseSvg from "@/modules/license/components/FrontOfLicenseSvg.vue";
import FrontOfLicenseDesktopSvg from "@/modules/license/components/FrontOfLicenseDesktopSvg.vue";
import Loader from "@/components/svg/Loader.vue";
import { base64toBlob } from "@/utils/index";

import { namespace } from "vuex-class";
import heic2any from "heic2any";
import axios from "axios";

const Document = namespace("document");

@Component({
  metaInfo() {
    return {
      title: "Orba One | Take Your License",
    };
  },
  components: {
    "front-of-license-svg": FrontOfLicenseSvg,
    "front-of-license-desktop-svg": FrontOfLicenseDesktopSvg,
    "previous-page-arrow": PreviousPageArrow,
    loader: Loader,
  },
})
export default class CaptureFrontLicense extends Vue {
  @Document.Action("upload") upload!: (payload: UploadPayload) => Promise<{}>;
  @Document.Getter("getCountryCode") countryCode!: string;
  @Ref() previewDocument!: HTMLImageElement;
  base64Image = "";
  isImageLoaded = false;
  isUploading = false;
  isUploadingError = false;
  isValidationError = false;
  imageBlob?: Blob;
  errorMessage = "";

  async mounted() {
    try {
      if (Vue.prototype.$useAudioInstructions) {
        Vue.prototype.$audioConfigs.stopChooseDocument?.();
        await Vue.prototype.$audioConfigs.playLicenceFront?.();
      }
    } catch (err) {
      console.warn("Audio playback failed:", err);
    }
  }

  previewImage(files: FileList | File[]) {
    const file: File = files[0];
    if (!file) return;

    const fileName = file.name.toLowerCase();

    // Reset states
    this.isValidationError = false;

    // 🚫 Block PDF uploads
    if (file.type === "application/pdf" || fileName.endsWith(".pdf")) {
      this.showErrorToast(
        "PDF files are not supported. Please upload an image instead."
      );
      return;
    }

    const reader = new FileReader();

    const processFile = (blob: Blob) => {
      const newReader = new FileReader();
      newReader.onload = async (event) => {
        if (event && event.target) {
          this.base64Image = event.target.result as string;
          this.imageBlob = await base64toBlob(this.base64Image);
          this.isImageLoaded = true;

          if (Vue.prototype.$useAudioInstructions) {
            Vue.prototype.$audioConfigs.stopLicenceFront();
            await Vue.prototype.$audioConfigs.playLicenceFrontReadability();
          }
        }
      };
      newReader.readAsDataURL(blob);
    };

    // 📸 Handle HEIC/HEIF files
    if (
      file.type === "image/heic" ||
      file.type === "image/heif" ||
      fileName.endsWith(".heic") ||
      fileName.endsWith(".heif")
    ) {
      heic2any({ blob: file, toType: "image/jpeg", quality: 0.9 })
        .then((result: Blob | Blob[]) => {
          const convertedBlob = Array.isArray(result) ? result[0] : result;
          processFile(convertedBlob);
        })
        .catch((err: any) => {
          console.error("HEIC conversion failed:", err);
          this.showErrorToast(
            "Failed to convert HEIC image. Please try again or use a JPEG/PNG."
          );
        });
    } else {
      // ✅ Standard image formats
      reader.onload = async (event) => {
        if (event && event.target) {
          this.base64Image = event.target.result as string;
          this.imageBlob = await base64toBlob(this.base64Image);
          this.isImageLoaded = true;

          if (Vue.prototype.$useAudioInstructions) {
            Vue.prototype.$audioConfigs.stopLicenceFront();
            await Vue.prototype.$audioConfigs.playLicenceFrontReadability();
          }
        }
      };
      reader.readAsDataURL(file);
    }
  }
  showErrorToast(message: string) {
    this.isValidationError = true;

    // Optionally store a dynamic message
    (this as any).errorMessage = message;

    // Auto-hide after 4 seconds
    setTimeout(() => {
      this.isValidationError = false;
    }, 5000);
  }
  retryImage() {
    this.isImageLoaded = false;
    this.isValidationError = false;
  }
  confirmImage() {
    this.uploadFrontLicense();
  }
  uploadFrontLicense(): void {
    const params = new URLSearchParams(window.location.search);
    const publicKey = params.get("publicKey");
    const applicantId = params.get("applicantId");

    const formData = new FormData();

    if (publicKey && applicantId) {
      formData.append("applicantId", applicantId);
      formData.append("publicKey", publicKey);
      formData.append("type", DocumentType.DRIVERSLICENSE);
      formData.append("side", DocumentSide.FRONT);
      formData.append("countryCode", this.countryCode);

      if (this.imageBlob) {
        formData.append(
          `file`,
          this.imageBlob,
          `license_front_${applicantId}_${publicKey}.png`
        );
      }

      this.isUploading = true;
      this.isUploadingError = false;

      // eslint-disable-next-line @typescript-eslint/no-this-alias
      const self = this;

      this.upload(formData)
        .then(() => {
          self.uploadLicenceFront();
          this.isUploading = false;
          this.$router.push({
            name: "captureLicenseBack",
            query: this.$route.query,
          });
        })
        .catch(() => {
          this.isValidationError = true;
          this.isUploading = false;
          this.isUploadingError = true;

          if (Vue.prototype.$useAudioInstructions) {
            Vue.prototype.$audioConfigs.playLicenceFrontReadability();
          }
          if (Vue.prototype.$useAudioInstructions) {
            Vue.prototype.$audioConfigs.playLicenceFrontFailed();
          }
        });
    }
  }

  isMobile(): boolean {
    if (screen.width <= 760) {
      return true;
    } else {
      return false;
    }
  }

  uploadLicenceFront() {
    const params = new URLSearchParams(window.location.search);
    const publicKey = params.get("publicKey");
    const applicantId = params.get("applicantId");
    const magicLink = params.get("magic");
    const progressionStep = 12;

    if ((publicKey && applicantId) || magicLink) {
      axios.put(
        `${process.env.VUE_APP_API_URL}/applicants/${applicantId}/verification-progress-step`,
        {
          publicKey,
          progressionStep,
        },
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
    }
  }
}
